import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
	Box,
	type FilledInputProps,
	FormControl,
	IconButton,
	InputAdornment,
	type OutlinedInputProps,
	type SxProps,
	TextField,
	type Theme,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha, createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import type { TextFieldProps } from "@mui/material/TextField";
import { ClearIcon } from "@mui/x-date-pickers";
import type React from "react";
import { type FC, useCallback, useEffect, useId, useMemo, useRef } from "react";
import { ALPHA, INK, STATUS } from "@/theme/tokens";

export interface ValidationTextFieldProps {
	validationCallback: (value: string) => boolean;
	helperText?: {
		valid?: string;
		invalid?: string;
	};
	validColor?: string;
	invalidColor?: string;
	performValidation: "always" | "onlyAfterInitialChange";
	showValidWhenValid: "always" | "never"; // Determines if valid state should show helper text when valid
}

export type SystemTextFieldProps = {
	validation?: ValidationTextFieldProps;
	label?: string;
	value?: string;
	onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
	defaultBorderColor?: string;
	defaultHelperText?: string;
	required?: boolean;
	variant?: "standard" | "filled" | "outlined";
	defaultErrorColor?: string;
	defaultValidColor?: string;
	reserveHeightForHelperText?: boolean;
	formControlSx?: SxProps<Theme>;
	inputSx?: SxProps<Theme>;
	inputType?: React.InputHTMLAttributes<unknown>["type"];
	maxValue?: number;
	submitTriggerred?: boolean;
	disabled?: boolean;
	placeholder?: string;
	/**
	 * Keep the label above the field, so `placeholder` shows while the field is empty and goes as soon as
	 * the user types. Without it MUI draws an empty field's label inside the box and hides the placeholder
	 * until focus. Off by default, so existing fields look the same.
	 */
	showPlaceholderWithLabel?: boolean;
	inputProps?: Partial<FilledInputProps>;
	injectedProps?: Omit<TextFieldProps, "variant">;
	onClear?: () => void;
	/**
	 * Stop the clear button's click from bubbling to React ancestors, and defer `onClear` past this click's
	 * native dispatch (default true). Both matter: the button only renders while `value` is truthy, so calling
	 * `onClear` synchronously unmounts it mid-click — by the time an ancestor's own native `click` listener
	 * (e.g. a portaled popover's outside-click detector, which lives outside React's synthetic event system
	 * entirely, so `stopPropagation` alone can't reach it) runs, `event.target` is already a detached node and
	 * its DOM-containment check silently fails, closing the ancestor. Deferring the state update lets the full
	 * native bubble phase finish against a still-attached target first. Set to `false` for a consumer that
	 * genuinely wants clearing to happen (and any bubbling) immediately.
	 */
	deferClearClick?: boolean;
	/**
	 * Keep the clear button's space reserved even while `value` is empty, so the field's own width
	 * doesn't jump when a value is entered/cleared — rendered disabled (MUI's dimmed disabled look)
	 * rather than omitted, so it reads as "nothing to clear yet" instead of a stray active control.
	 * Off by default — every pre-existing `onClear` caller keeps its current (icon-appears-with-a-value)
	 * behavior; opt in per field where that jump is visible (e.g. sitting inline in a filter row next
	 * to fixed-width siblings).
	 */
	reserveClearWidth?: boolean;
	isInsideAutoComplete?: boolean;
	autoFocus?: boolean;
	labelSx?: SxProps<Theme>;
	fullWidth?: boolean;
	/**
	 * Ink for the helper line while it is showing `defaultHelperText` (no validation verdict yet).
	 *
	 * That text otherwise inherits `defaultBorderColor` — a ~20%-opacity border tint that is fine as
	 * a hairline but is not a readable text color. Pass a real ink (e.g. `INK.MUTED`) when the helper
	 * is guidance the user is meant to READ rather than a state hint. Validation colors still win
	 * once there is a verdict, so an error never renders in this ink.
	 */
	helperTextColor?: string;
	/**
	 * Border ink while the field has focus and there is no validation verdict yet.
	 *
	 * Without it the focused outline keeps `defaultBorderColor` — it thickens to 2px but stays a
	 * ~20% tint, so focus is effectively invisible. (The component does carry a focus rule, but it
	 * is keyed `&.Mui-focused` on the TextField ROOT, where MUI never puts that class — it lands on
	 * the InputBase — so it has never applied.) Pass a real ink on fields where losing the focus
	 * ring matters, e.g. an autofocused first field. A validation verdict still wins.
	 */
	focusBorderColor?: string;
	dataTestId?: string;
	/**
	 * Tooltip text to display next to the label with an info icon on hover.
	 */
	labelTooltip?: string;
};

/**
 * A text field component that provides validation feedback based on user input.
 * @param onChange
 * @param defaultBorderColor
 * @param validation
 * @param value
 * @param defaultHelperText
 * @param label
 * @param required
 * @param variant
 * @param defaultValidColor
 * @param defaultErrorColor
 * @param reserveHeightForHelperText - Whether to reserve height for helper text even when it's empty.
 * @param inputSx
 * @param formControlSx
 * @param inputType - The type of the input field (e.g., "text", "email").
 * @param maxValue - Maximum value allowed for number inputs (only applies when inputType="number").
 * @param submitTriggerred - Whether the form has been submitted, if submitted but validation is not yet performed, it will now perform validation.
 * @param disabled
 * @param placeholder
 * @param inputProps - Additional props for the input element.
 * @param injectedProps - In case you need to use the component inside another mui component, you can pass props that are not defined in this component.
 * @param onClear - allows you to clear the input value via button click.
 * @param deferClearClick - stop the clear button's click from bubbling and defer onClear past the native dispatch (default true).
 * @param reserveClearWidth - keep the clear button's width reserved (hidden, not omitted) even while value is empty (default false).
 * @param isInsideAutoComplete - if the textfield is rendered inside an autocomplete component. TODO: this should be removed when mui pass proper textfield props without the faulty injection that collides with deprecation(they pass injectedProps?.InputProps instead of injectedProps?.slotProps?.input)
 * @param onKeyDown - optional keydown handler
 * @param autoFocus - whether to autoFocus the input on mount
 * @param labelSx - styles for the label
 */
export const SystemValidationTextField: FC<SystemTextFieldProps> = ({
	onChange,
	defaultBorderColor = alpha(INK.SECONDARY, ALPHA.FIELD_OUTLINE),
	validation,
	value = "",
	defaultHelperText = "",
	label,
	required = false,
	variant = "outlined",
	defaultValidColor = STATUS.SUCCESS.main,
	defaultErrorColor = STATUS.ERROR.main,
	reserveHeightForHelperText = true,
	inputSx,
	formControlSx,
	inputType,
	maxValue,
	submitTriggerred = false,
	disabled,
	placeholder,
	showPlaceholderWithLabel = false,
	inputProps,
	injectedProps,
	onClear,
	deferClearClick = true,
	reserveClearWidth = false,
	isInsideAutoComplete,
	onKeyDown,
	autoFocus,
	labelSx,
	fullWidth,
	helperTextColor,
	focusBorderColor,
	dataTestId,
	labelTooltip,
}) => {
	const parentTheme = useTheme();
	// Was derived from the translated `label`, so "Email or phone number" produced an id containing
	// spaces. `aria-describedby` is a space-separated IDREF list, so that resolved to four ids that do
	// not exist and the helper text was never announced — and two fields sharing a label collided.
	const helperTextId = useId();

	const modifiedTheme = useMemo(
		() =>
			createTheme({
				/*Perform local override*/
				...parentTheme,
				components: {
					...parentTheme.components,
					MuiFormLabel: {
						...parentTheme.components?.MuiFormLabel,
						styleOverrides: {
							...(parentTheme.components?.MuiFormLabel?.styleOverrides || {}),
							asterisk: {
								color: STATUS.ERROR.main,
								fontWeight: 700,
							},
						},
					},
				},
			}),
		[parentTheme],
	);
	const initialChangeRef = useRef(false);

	useEffect(() => {
		if (submitTriggerred && !initialChangeRef.current) {
			initialChangeRef.current = true;
		}
	}, [initialChangeRef, submitTriggerred]);

	const isValid = useMemo(() => {
		if (!validation || (validation.performValidation === "onlyAfterInitialChange" && !initialChangeRef.current)) {
			return undefined;
		}
		return validation.validationCallback(value);
	}, [value, validation, initialChangeRef.current]);

	const borderColor = useMemo(() => {
		if (!validation || isValid === undefined) return defaultBorderColor;
		if (isValid && validation.showValidWhenValid === "always") return validation.validColor || defaultValidColor;
		if (!isValid) return validation.invalidColor || defaultErrorColor;
		return defaultBorderColor;
	}, [isValid, validation, defaultBorderColor]);

	const helperText = useMemo(() => {
		if (!validation || isValid === undefined) return defaultHelperText;
		if (isValid && validation.showValidWhenValid === "always") return validation.helperText?.valid || "";
		if (!isValid) return validation.helperText?.invalid || "";
		return defaultHelperText;
	}, [isValid, validation, defaultHelperText]);

	const handleChange = useCallback<React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
		(e) => {
			if (!initialChangeRef.current) {
				initialChangeRef.current = true;
			}

			if (inputType === "number") {
				const numValue = Number(e.target.value);
				if (maxValue && numValue > maxValue) {
					e.target.value = String(maxValue);
				}
			}

			onChange?.(e);
		},
		[onChange, inputType, maxValue],
	);

	const reserveExtraSpace = useMemo(() => !helperText && reserveHeightForHelperText, [helperText, reserveHeightForHelperText]);

	// Only the no-verdict state is overridable — an invalid/valid verdict keeps its status color.
	const helperInk = useMemo(() => (isValid === undefined ? (helperTextColor ?? borderColor) : borderColor), [isValid, helperTextColor, borderColor]);
	const focusedBorderColor = useMemo(() => (isValid === undefined ? (focusBorderColor ?? borderColor) : borderColor), [isValid, focusBorderColor, borderColor]);

	const InputProps: Partial<FilledInputProps> | Partial<OutlinedInputProps> | undefined = useMemo(
		() => ({
			sx: {
				"& .MuiOutlinedInput-notchedOutline": {
					borderColor,
				},
				"&:hover .MuiOutlinedInput-notchedOutline": {
					borderColor,
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderColor: focusedBorderColor,
				},
			},
			...(onClear &&
				(value || reserveClearWidth) && {
					endAdornment: (
						<InputAdornment position={"end"}>
							<IconButton
								onClick={(event) => {
									if (!deferClearClick) {
										onClear?.();
										return;
									}
									event.stopPropagation();
									// Let this click's native bubble phase finish (reaching e.g. a portaled ancestor
									// popover's own outside-click listener) against a still-attached target before
									// the value change unmounts this button — see `deferClearClick` doc above.
									setTimeout(() => onClear?.(), 0);
								}}
								size={"small"}
								// Reserved-but-empty state: kept mounted and visible (in the standard MUI
								// disabled look) purely for layout, so the field's width doesn't jump when a
								// value is entered/cleared. `disabled` also drops it from the tab order and
								// no-ops clicks, so there's nothing to press when there's nothing to clear.
								disabled={!value}
							>
								<ClearIcon fontSize={"small"} />
							</IconButton>
						</InputAdornment>
					),
				}),
			...inputProps,
			...injectedProps?.slotProps?.input,
		}),
		[borderColor, focusedBorderColor, onClear, value, deferClearClick, reserveClearWidth, inputProps, injectedProps?.slotProps?.input],
	);

	return (
		<ThemeProvider theme={modifiedTheme}>
			<FormControl required={required} aria-required={required} sx={formControlSx} disabled={disabled} fullWidth={fullWidth} data-testid={dataTestId}>
				<TextField
					{...injectedProps}
					autoFocus={autoFocus ?? injectedProps?.autoFocus}
					placeholder={placeholder}
					required={required}
					disabled={disabled}
					size={"small"}
					variant={variant}
					value={value}
					onChange={handleChange}
					// Both handlers run: the dedicated prop and one passed through `injectedProps`. Setting the prop after the
					// spread used to replace the injected one with `undefined`, so a caller's Enter handler silently did nothing.
					onKeyDown={(event) => {
						injectedProps?.onKeyDown?.(event);
						onKeyDown?.(event);
					}}
					label={
						labelTooltip ? (
							<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
								{label}
								<Tooltip title={labelTooltip}>
									<InfoOutlined sx={{ fontSize: "16px", color: INK.MUTED, cursor: "help" }} />
								</Tooltip>
							</Box>
						) : (
							label
						)
					}
					aria-describedby={helperTextId}
					aria-label={label}
					// Only once there IS a verdict. `!isValid` is `true` while `isValid` is undefined, so
					// every field without a `validation` prop announced itself invalid from first render.
					// NOTE: this attribute (like the component's other ARIA) sits on the TextField root, not
					// the native input — MUI owns the input's `aria-invalid` via its `error` prop. Moving the
					// component's ARIA onto the input is the right end state but changes behavior for all
					// ~51 callers, so it is deliberately out of scope here.
					aria-invalid={isValid === undefined ? undefined : !isValid}
					sx={{
						...(disabled && { opacity: 0.7, pointerEvents: "none" }),
						"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: alpha(INK.SECONDARY, 0.25),
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							border: `1px solid ${alpha(INK.SECONDARY, 0.8)}`,
						},
						...inputSx,
					}}
					type={inputType}
					slotProps={{
						...injectedProps?.slotProps,

						inputLabel: {
							shrink: inputType === "date" || inputType === "time" || showPlaceholderWithLabel ? true : undefined,
							sx: labelSx,
						},

						...(!isInsideAutoComplete && { input: InputProps }),
					}}
				/>
				<Box
					id={helperTextId}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "start",
					}}
				>
					{isValid === false && (
						<InfoOutlined
							sx={{
								mr: 1,
								color: validation?.invalidColor || defaultErrorColor,
								fontSize: "14px",
							}}
						/>
					)}
					<Typography
						style={{
							fontWeight: 400,
							fontSize: "12px",
							lineHeight: "19px",
							opacity: reserveExtraSpace ? 0 : 1,
							pointerEvents: reserveExtraSpace ? "none" : "auto",
							color: helperInk,
						}}
					>
						{reserveExtraSpace ? "placeholder" : helperText}
					</Typography>
				</Box>
			</FormControl>
		</ThemeProvider>
	);
};
